import { animate, style, transition, trigger } from '@angular/animations';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { ScreenService } from '@catch/services';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TooltipDirective } from '@catch/directives';
import { TagComponent, TagVariant } from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { TourAnchorMatMenuDirective, TourMatMenu } from 'ngx-ui-tour-md-menu';
import { PopoverModule } from 'primeng/popover';
import { fadeInOut } from '../../../assets/animations/animations';
import { AppState } from '../../app.reducer';
import { transitionString } from '../../constants/transition';
import { CompanyUserRole } from '../../enums/company-user.role.enum';
import { StepRole } from '../../enums/step-role.enum';
import { SubscriptionType } from '../../enums/subscription-type.enum';
import { CmdkService } from '../../services/cmdk.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import { RouterService } from '../../services/router.service';
import { SearchService } from '../../services/search.service';
import { SidenavService } from '../../services/sidenav.service';
import { hasPermission } from '../../store/auth/auth.reducer';
import { closeSidenav, toggleSidenav } from '../../store/config/config.actions';
import { selectSidenavOpen } from '../../store/config/config.reducer';
import {
  selectExportsCount,
  selectShowDownloadsSection,
} from '../../store/export-downloads/export-downloads.reducer';
import { selectNotificationsUnseen } from '../../store/notifications/notifications.reducer';
import {
  selectSubscription,
  selectSubscriptionLoaded,
} from '../../store/subscription/subscription.reducer';
import { selectUserAccount } from '../../store/user-account/user-account.reducer';
import { selectUserAvatarUrl } from '../../store/user-avatar/user-avatar.reducer';
import { ButtonComponent } from '../button/button.component';
import { CmdkComponent } from '../cmdk/cmdk.component';
import { CreditsCounterExpandedComponent } from '../credits-counter-expanded/credits-counter-expanded.component';
import { CreditsCounterComponent } from '../credits-counter/credits-counter.component';
import { KbdComponent } from '../kbd/kbd.component';
import { SidenavDownloadsComponent } from './sidenav-downloads/sidenav-downloads.component';
import { SidenavNotificationsComponent } from './sidenav-notifications/sidenav-notifications.component';
import { AvatarPlaceholderComponent } from '../avatar-placeholder/avatar-placeholder.component';
import { SidenavUserComponent } from './sidenav-user/sidenav-user.component';

@UntilDestroy()
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    RouterModule,
    FontAwesomeModule,
    TooltipDirective,
    TagComponent,
    PopoverModule,
    CreditsCounterComponent,
    CreditsCounterExpandedComponent,
    SidenavUserComponent,
    SidenavNotificationsComponent,
    SidenavDownloadsComponent,
    CmdkComponent,
    KbdComponent,
    ButtonComponent,
    TourAnchorMatMenuDirective,
    TourMatMenu,
    AvatarPlaceholderComponent,
  ],
  animations: [
    trigger('menuChange', [
      transition('void => main', [
        style({ opacity: 0, transform: 'translateX(-20%) scale(0.9)' }),
        animate(
          transitionString,
          style({ opacity: 1, transform: 'translateX(0) scale(1)' }),
        ),
      ]),
      transition('main => void', [
        style({ opacity: 1, transform: 'translateX(0) scale(1)' }),
        animate(
          transitionString,
          style({ opacity: 0, transform: 'translateX(-20%) scale(0.9)' }),
        ),
      ]),
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20%) scale(0.9)' }),
        animate(
          transitionString,
          style({ opacity: 1, transform: 'translateX(0) scale(1)' }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateX(0) scale(1)' }),
        animate(
          transitionString,
          style({ opacity: 0, transform: 'translateX(20%) scale(0.9)' }),
        ),
      ]),
    ]),
    fadeInOut,
  ],
  //changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent {
  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly screenService = inject(ScreenService);
  private readonly document = inject(DOCUMENT);
  private readonly cmdkService = inject(CmdkService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly sidenavService = inject(SidenavService);
  public readonly themingService = inject(ThemingService);
  public readonly routerService = inject(RouterService);
  public readonly searchService = inject(SearchService);

  public readonly TagVariant = TagVariant;

  sidenavOpen = this.store.selectSignal(selectSidenavOpen);
  isSmallScreen = toSignal(this.screenService.isSmallScreen$, {
    initialValue: this.screenService.isSmallScreen,
  });
  isSidenavCollapsed = computed(
    () => !this.sidenavOpen() && !this.isSmallScreen(),
  );
  private readonly mobileShell =
    viewChild<ElementRef<HTMLElement>>('mobileShell');

  readonly popoverStyleClass = computed(() => {
    const classes = ['sidenav-popover'];
    if (!this.isSmallScreen() && !this.sidenavOpen()) {
      classes.push('collapsed');
    }
    return classes.join(' ');
  });

  readonly popoverAppendTo = computed(() => {
    if (!this.isSmallScreen()) {
      return 'body';
    }
    return this.mobileShell()?.nativeElement ?? 'body';
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.isSmallScreen()) {
          this.store.dispatch(closeSidenav());
        }
      });
  }
  public isAdmin = this.store.selectSignal(
    hasPermission(CompanyUserRole.ADMIN),
  );
  public avatarUrl = this.store.selectSignal(selectUserAvatarUrl);
  private readonly avatarImageFailed = signal(false);
  public showAvatarImage = computed(
    () => !!this.avatarUrl() && !this.avatarImageFailed(),
  );
  public userAccount = this.store.selectSignal(selectUserAccount);

  private readonly resetAvatarImageFailed = effect(() => {
    this.avatarUrl();
    this.avatarImageFailed.set(false);
  });
  public showDownloads = this.store.selectSignal(selectShowDownloadsSection);
  public exportsCount = this.store.selectSignal(selectExportsCount);
  public notificationsUnseen = this.store.selectSignal(
    selectNotificationsUnseen,
  );
  public subscription = this.store.selectSignal(selectSubscription);
  public subscriptionLoaded = this.store.selectSignal(selectSubscriptionLoaded);

  public readonly SubscriptionType = SubscriptionType;
  public readonly StepRole = StepRole;

  readonly isMac = computed(() =>
    (this.document.defaultView?.navigator.platform ?? '')
      .toLowerCase()
      .includes('mac'),
  );

  readonly cmdkKeys = computed(() =>
    this.isMac() ? (['⌘', 'K'] as const) : (['Strg', 'K'] as const),
  );

  readonly cmdkAriaLabel = computed(() =>
    this.isMac() ? 'Command + K' : 'Control + K',
  );

  openCmdkFromSidenav(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.cmdkService.openCmdk();
  }

  toggleSidenav(): void {
    this.store.dispatch(toggleSidenav());
  }

  closeSidenav(): void {
    this.store.dispatch(closeSidenav());
  }

  getUserInitials(): string {
    const account = this.userAccount();
    return `${account?.firstName?.[0] ?? ''}${account?.lastName?.[0] ?? ''}`.toUpperCase();
  }

  onAvatarImageError(): void {
    this.avatarImageFailed.set(true);
  }
}
